import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ArchivesListTable from "./ArchivesListTable";
import { Filters, useFetchArchives } from "./hook/useFetchArchives";
import { CustomerListPagination } from "../../ManageCustomers/components/customer-pagination";
import { Input } from "src/components/input/input";
import { setSearchParamsFilter } from "src/utils/searchParam";
import { useDebounce } from "use-debounce";

const ArchivedList = () => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const name = searchParams.get("fullname") || "";
  const employee_code = searchParams.get("employee_code") || "";

  const page = parseInt(searchParams.get("page") || "1", 10);
  const pagesize = 20;

  const [debouncedName] = useDebounce(name, 500);
  const [debouncedCode] = useDebounce(employee_code, 500);

  const [failSafePageNumber, setFailSafePageNumber] = useState<number>(1);

  const filter: Filters = {
    page,
    pagesize,
    name: debouncedName,
    employee_code: debouncedCode,
  };

  const {
    data: archives = [],
    isLoading,
    isFetching,
  } = useFetchArchives(filter);

  const hasNextPage = archives?.length === pagesize;
  const hasPrevPage = page > 1;

  useEffect(() => {
    if (!archives) return;
    if (archives?.length > 0) {
      setFailSafePageNumber(page);
    } else if (failSafePageNumber !== page) {
      toast.info("No more Archives!");
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: failSafePageNumber.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archives]);

  const handleNextPage = () => {
    setFailSafePageNumber(page);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (page === 1) {
      toast.info("This is the first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  const clearFilter = () => {
    setSearchParams();
  }

  return (
    <div>
      <div className="flex justify-between mb-4 sticky top-0 backdrop-blur-lg">
        <div className="flex gap-4 justify-center items-center">
          <button
            className="p-2 rounded-full bg-gray-200 flex justify-center items-center hover:bg-gray-300 transition duration-300"
            onClick={() => navigate("/hr/employees")}
          >
            <ArrowLeft className="size-3.5" />
          </button>
          <h2 className="text-xl font-semibold text-left">
            Archived Employees
          </h2>
        </div>

        <div className="flex gap-4 justify-between items-center">
          <Input
            type="text"
            value={name}
            placeholder="Search Employee Name..."
            onChange={(e) =>
              setSearchParamsFilter(
                { fullname: e.target.value.toLowerCase() },
                setSearchParams,
              )
            }
            className="border w-60"
          />
          <Input
            type="text"
            value={employee_code}
            placeholder="Search Employee Code..."
            onChange={(e) =>
              setSearchParamsFilter(
                { employee_code: e.target.value.toLowerCase() },
                setSearchParams,
              )
            }
            className="border w-60"
          />

          <button
            className="px-3 py-1 rounded-lg bg-gray-100 h-fit w-fit hover:bg-red-50 hover:shadow-lg text-sm transition-all duration-300"
            onClick={clearFilter}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="bg-card rounded-xl shadow-lg p-6">
        {isLoading && (
          <p className="text-gray-500">
            Loading
            <span className="loading loading-dots loading-sm"></span>
          </p>
        )}
        {!isLoading && archives.length === 0 && (
          <p className="text-gray-500">No employees found.</p>
        )}
        <ArchivesListTable archives={archives} />
      </div>
      <div className="flex-grow my-3">
        <CustomerListPagination
          currentPageNumber={page}
          onNext={handleNextPage}
          onPrev={handlePrevPage}
          onJumpToPage={handleJumpToPage}
          isFetching={isFetching}
          hasPrev={hasPrevPage}
          hasNext={hasNextPage}
        />
      </div>
    </div>
  );
};

export default ArchivedList;
